import { useQuery } from 'react-query';
import { axios } from '@/lib/axios';
import { useNotificationStore } from '@/stores/notifications';
import storage from '@/utils/storage';
import { API_URL } from '@/config';

interface LogEntry {
  message: string;
  timestamp: string;
  level: string;
  source: string;
  metadata: {
    rawLog: string;
    category: string | null;
  };
}

interface LogSummary {
  hasErrors: boolean;
  hasWarnings: boolean;
  sources: string[];
}

interface LogResponse {
  log_counter: number;
  cluster_id: string;
  timestamp: string;
  summary: LogSummary;
  logs: LogEntry[];
  __v: number;
}

export const getClusterLogs = async (cluster_id: string, counter: number): Promise<LogResponse> => {
  const access_token = storage.getToken();

  if (!access_token || !cluster_id) {
    throw new Error('Missing required parameters');
  }

  try {
    const response = await axios.get(`${API_URL}/v2/cluster/${cluster_id}/logs`, {
      params: {
        access_token,
        counter,
      },
    });
    return response?.data;
  } catch (error) {
    throw error;
  }
};

export const useGetClusterLogs = (cluster_id: string, counter: number, config = {}) => {
  const { addNotification } = useNotificationStore();

  return useQuery<LogResponse, Error>({
    queryKey: ['clusterLogs', cluster_id, counter],
    queryFn: () => getClusterLogs(cluster_id, counter),
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Failed to fetch logs',
        message: error.message,
      });
    },
    onSuccess: (data) => {},
    ...config,
  });
};
